import { Component, ErrorInfo, ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { AlertTriangle } from "src/assets/mapfre-icons-components";
import { Button } from "../ui/button";
import { HeadlineSize8Medium, CaptionMLow } from "../ui/typographic";
import { buildErrorContext } from "src/utils/errorContext";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ReactErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const context = buildErrorContext();

    Sentry.captureException(error, {
      extra: {
        componentStack: errorInfo.componentStack,
        ...context,
      },
    });

    this.setState({
      error,
      errorInfo,
    });
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  private getErrorMessage(error: Error): string {
    if (error.message.includes("Objects are not valid as a React child")) {
      return "Ocorreu um erro ao renderizar os dados. Verifique se não há objetos sendo exibidos diretamente no componente.";
    }

    if (error.message.includes("Minified React error #31")) {
      return "Erro de renderização: Foi encontrado um objeto onde deveria haver texto ou componente. Verifique os dados que estão sendo exibidos.";
    }

    if (error.message.includes("Cannot read properties")) {
      return "Erro ao acessar propriedades de um objeto. Verifique se os dados estão disponíveis antes de utilizá-los.";
    }

    return error.message || "Ocorreu um erro inesperado na aplicação.";
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex h-screen w-full items-center justify-center overflow-hidden bg-gray-50 p-4">
          <div className="flex flex-col items-center justify-center gap-6 rounded-lg bg-white p-8 shadow-lg">
            <div className="bg-bg-error-1 flex size-16 items-center justify-center rounded-full">
              <AlertTriangle className="text-state-error size-8" />
            </div>

            <div className="space-y-2 text-center">
              <HeadlineSize8Medium className="text-brand-blue-1">
                Algo deu errado!
              </HeadlineSize8Medium>
              <CaptionMLow className="text-gray-600">
                {this.getErrorMessage(
                  this.state.error ?? new Error("Unknown error"),
                )}
              </CaptionMLow>
            </div>

            <div className="w-full space-y-3">
              <Button
                variant="primary"
                size="primary"
                onClick={this.handleReload}
                className="w-full"
              >
                Recarregar página
              </Button>

              <Button
                variant="outline"
                size="primary"
                onClick={this.handleReset}
                className="w-full"
              >
                Tentar novamente
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ReactErrorBoundary;
